import React from 'react';
import {
  Grid, 
  List, 
  Card, 
  CardHeader, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Checkbox, 
  Button, 
  Divider
} from '@mui/material';

interface TransferListProps {
  leftItems: string[];
  rightItems: string[];
  onChange: (leftItems: string[], rightItems: string[]) => void;
  itemLabel?: (item: string) => string;
}

const TransferList: React.FC<TransferListProps> = ({
  leftItems,
  rightItems,
  onChange,
  itemLabel = (item) => item,
}) => {
  // State for selected items
  const [checkedLeft, setCheckedLeft] = React.useState<string[]>([]);
  const [checkedRight, setCheckedRight] = React.useState<string[]>([]);
  
  // Store the original order of items when component mounts
  const originalOrder = React.useRef<string[]>([...leftItems]);

  // Update originalOrder only on initial mount or when leftItems changes
  React.useEffect(() => {
    if (!originalOrder.current.length) {
      originalOrder.current = [...leftItems];
    }
  }, [leftItems]);

  // Handle toggling checkbox selection
  const handleToggle = (value: string, isLeft: boolean) => () => {
    if (isLeft) {
      const currentIndex = checkedLeft.indexOf(value);
      const newChecked = [...checkedLeft];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setCheckedLeft(newChecked);
    } else {
      const currentIndex = checkedRight.indexOf(value);
      const newChecked = [...checkedRight];

      if (currentIndex === -1) {
        newChecked.push(value);
      } else {
        newChecked.splice(currentIndex, 1);
      }

      setCheckedRight(newChecked);
    }
  };

  // Move items from left to right
  const handleMoveRight = () => {
    const newRightItems = [...rightItems, ...checkedLeft];
    const newLeftItems = leftItems.filter(item => !checkedLeft.includes(item));

    onChange(newLeftItems, newRightItems);
    setCheckedLeft([]);
  };

  // Move items from right to left while maintaining original order
  const handleMoveLeft = () => {
    const itemsToMove = [...checkedRight];
    const newRightItems = rightItems.filter(item => !checkedRight.includes(item));
    
    // Create new left items maintaining original order
    const newLeftItems = originalOrder.current.filter(item => 
      [...leftItems, ...itemsToMove].includes(item)
    );

    onChange(newLeftItems, newRightItems);
    setCheckedRight([]);
  };

  // Move all items from left to right
  const handleMoveAllRight = () => {
    onChange([], [...rightItems, ...leftItems]);
    setCheckedLeft([]);
  };

  // Move all items from right to left while maintaining original order
  const handleMoveAllLeft = () => {
    const newLeftItems = originalOrder.current.filter(item => 
      [...leftItems, ...rightItems].includes(item)
    );
    onChange(newLeftItems, []);
    setCheckedRight([]);
  };

  const customList = (title: string, items: string[], checked: string[], isLeft: boolean) => (
    <Card>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        title={title}
        titleTypographyProps={{ variant: 'subtitle1' }}
      />
      <Divider />
      <List
        sx={{
          width: '100%',
          height: 250,
          bgcolor: 'background.paper',
          overflow: 'auto',
        }}
        dense
        component="div"
        role="list"
      >
        {items.map((value) => {
          const labelId = `transfer-list-all-item-${value}-label`;

          return (
            <ListItem
              key={value}
              role="listitem"
              button
              onClick={handleToggle(value, isLeft)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{
                    'aria-labelledby': labelId,
                  }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={itemLabel(value)} />
            </ListItem>
          );
        })}
      </List>
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center">
      <Grid item xs={5}>
        {customList('Available Fields', leftItems, checkedLeft, true)}
      </Grid>
      <Grid item xs={2}>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleMoveRight}
            disabled={checkedLeft.length === 0}
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleMoveAllRight}
            disabled={leftItems.length === 0}
          >
            &raquo;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleMoveAllLeft}
            disabled={rightItems.length === 0}
          >
            &laquo;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleMoveLeft}
            disabled={checkedRight.length === 0}
          >
            &lt;
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={5}>
        {customList('Visible Fields', rightItems, checkedRight, false)}
      </Grid>
    </Grid>
  );
};

export default TransferList;